import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { Card, CardBody, CardHeader, CardTitle } from '../../components/ui/Card.js';
import { useAdelantosList } from '../../lib/queries/adelantos.js';

const STATUS_LABEL: Record<string, { label: string; tone: 'warning' | 'success' | 'danger' }> = {
  borrador: { label: 'Borrador', tone: 'warning' },
  otorgado: { label: 'Otorgado', tone: 'success' },
  revocado: { label: 'Revocado', tone: 'danger' },
};

export function AdelantosList() {
  const [ninaEntityId, setNinaEntityId] = useState<string>('');
  const [tipo, setTipo] = useState<string>('');
  const [estatus, setEstatus] = useState<string>('');
  const filters: { nina_entity_id?: string; tipo_adelanto?: string; estatus?: string } = {};
  if (ninaEntityId) filters.nina_entity_id = ninaEntityId;
  if (tipo) filters.tipo_adelanto = tipo;
  if (estatus) filters.estatus = estatus;
  const { data, isLoading, error } = useAdelantosList(filters);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-distrito-700">Adelantos</h1>
        <Link to="/adelantos/new">
          <Button variant="primary">+ Nuevo adelanto</Button>
        </Link>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filtros</CardTitle>
        </CardHeader>
        <CardBody>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-sm font-medium text-gray-700">Nina entity_id</span>
              <input
                className="px-3 py-2 border border-gray-300 rounded-md text-base font-mono text-xs"
                placeholder="01HY7Q..."
                value={ninaEntityId}
                onChange={(e) => setNinaEntityId(e.target.value)}
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-sm font-medium text-gray-700">Tipo</span>
              <select
                className="px-3 py-2 border border-gray-300 rounded-md text-base"
                value={tipo}
                onChange={(e) => setTipo(e.target.value)}
              >
                <option value="">Todos</option>
                <option value="investidura">Investidura</option>
                <option value="especialidad">Especialidad</option>
                <option value="insignia">Insignia</option>
                <option value="cargo">Cargo</option>
                <option value="etapa">Etapa</option>
              </select>
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-sm font-medium text-gray-700">Estatus</span>
              <select
                className="px-3 py-2 border border-gray-300 rounded-md text-base"
                value={estatus}
                onChange={(e) => setEstatus(e.target.value)}
              >
                <option value="">Todos</option>
                <option value="borrador">Borrador</option>
                <option value="otorgado">Otorgado</option>
                <option value="revocado">Revocado</option>
              </select>
            </label>
          </div>
        </CardBody>
      </Card>

      {isLoading && <div className="text-center py-8 text-gray-600">Cargando…</div>}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-md text-red-700" role="alert">
          Error: {error.message}
        </div>
      )}

      {data && (
        <Card>
          <CardBody>
            {data.data.data.length === 0 ? (
              <p className="text-center py-8 text-gray-500">
                Sin resultados para los filtros aplicados.
              </p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-distrito-50 border-b border-distrito-100">
                  <tr className="text-left">
                    <th className="px-4 py-3 font-bold text-xs uppercase tracking-wider text-distrito-700">
                      Niña
                    </th>
                    <th className="px-4 py-3 font-bold text-xs uppercase tracking-wider text-distrito-700">
                      Tipo
                    </th>
                    <th className="px-4 py-3 font-bold text-xs uppercase tracking-wider text-distrito-700">
                      Nombre
                    </th>
                    <th className="px-4 py-3 font-bold text-xs uppercase tracking-wider text-distrito-700">
                      Estatus
                    </th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {data.data.data.map((a) => {
                    const status = STATUS_LABEL[a.estatus] ?? STATUS_LABEL.borrador;
                    return (
                      <tr
                        key={a.entity_id}
                        className="border-b border-gray-100 hover:bg-distrito-50/60 transition-colors"
                      >
                        <td className="px-4 py-3 font-mono text-xs">
                          {a.nina_entity_id.slice(0, 10)}…
                        </td>
                        <td className="px-4 py-3">{a.tipo_adelanto}</td>
                        <td className="px-4 py-3">{a.nombre_adelanto}</td>
                        <td className="px-4 py-3">
                          <Badge tone={status?.tone ?? 'warning'}>
                            {status?.label ?? a.estatus}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Link
                            to={`/adelantos/${a.entity_id}`}
                            className="text-distrito-600 hover:text-distrito-800 font-semibold"
                          >
                            Ver →
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </CardBody>
        </Card>
      )}
    </div>
  );
}
